import {
  createSelector,
  createAction,
  createSlice,
  createAsyncThunk
} from '@reduxjs/toolkit';

import { wallet } from 'utils/wallet';

const SLICE_NAME = 'status';

const initialState = {
  actionStatus: {},
  globalAlert: {},
  localAlert: {}
};

const checkAccountAvailable = createAsyncThunk(
  'CHECK_ACCOUNT_AVAILABLE',
  async (accountId: string) => {
    return await wallet.checkAccountAvailable(accountId);
  }
);

const showCustomAlert = createAction('SHOW_CUSTOM_ALERT', (payload) => ({
  payload
}));

const statusSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    clearLocalAlert(state) {
      state.localAlert = {};
    },
    clearGlobalAlert(state, { payload }) {
      state.globalAlert = !payload
        ? {}
        : Object.keys(state.globalAlert).reduce(
            (x, type) => ({
              ...x,
              ...(type !== payload
                ? {
                    [type]: state.globalAlert[type]
                  }
                : undefined)
            }),
            {}
          );
    }
  },
  extraReducers: (builder) => {
    builder.addDefaultCase((state, action) => {
      let { ready, payload } = action;
      const { error, type } = action;

      if (type.endsWith('/rejected') || type.endsWith('/fulfilled')) {
        payload = {
          ...payload,
          type: payload?.type || error?.type,
          messageCode: error?.messageCode,
          message: error?.message
        };
        ready = true;
      }

      const actionStatus = {
        ...state.actionStatus,
        [type]:
          ready || type === 'SHOW_CUSTOM_ALERT'
            ? {
                success:
                  typeof ready === 'undefined'
                    ? typeof payload?.success !== 'undefined'
                      ? payload?.success
                      : false
                    : ready
                    ? false
                    : undefined,
                errorType: payload?.type,
                errorMessage:
                  payload?.message ||
                  (type === 'SHOW_CUSTOM_ALERT' && payload?.errorMessage) ||
                  undefined,
                data: payload
              }
            : undefined
      };

      return {
        ...state,
        actionStatus,
        globalAlert: {
          ...state.globalAlert,
          [type]:
            payload?.showAlert === true
              ? {
                  show: typeof ready === 'undefined' ? true : ready && error,
                  messageCode: payload?.messageCode,
                  console:
                    error ||
                    (type === 'SHOW_CUSTOM_ALERT' &&
                      payload.errorMessage &&
                      true)
                }
              : undefined
        },
        localAlert:
          typeof ready === 'undefined'
            ? state.localAlert
            : type === 'CHECK_ACCOUNT_AVAILABLE/fulfilled' ||
              type === 'CHECK_ACCOUNT_AVAILABLE/rejected'
            ? {
                show:
                  type === 'CHECK_ACCOUNT_AVAILABLE/fulfilled' ||
                  type === 'CHECK_ACCOUNT_AVAILABLE/rejected'
                    ? true
                    : false,
                success: !error,
                messageCode: `reduxActions.${type}.${
                  ready ? (error ? 'error' : 'success') : 'pending'
                }`
              }
            : state.localAlert
      };
    });
  }
});

export default statusSlice;

export const actions = {
  checkAccountAvailable,
  showCustomAlert,
  ...statusSlice.actions
};

// Top level selectors
export const selectStatusSlice = (state: any) => state[SLICE_NAME];

export const selectStatusLocalAlert = createSelector(
  selectStatusSlice,
  (status) => status.localAlert || {}
);

export const selectStatusGlobalAlert = createSelector(
  selectStatusSlice,
  (status) => status.globalAlert || {}
);

export const selectStatusActionStatus = createSelector(
  selectStatusSlice,
  (status) => status.actionStatus || {}
);
